import {
  Button,
  DatePicker,
  Divider,
  Flex,
  Modal,
  Select,
  Space,
  Tooltip,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import type { SortOrder } from "antd/es/table/interface";

import styles from "./styles.module.scss";

import { useEffect, useMemo, useState } from "react";

import { DownloadOutlined, InfoCircleOutlined } from "@ant-design/icons";
import useDownloadPricingAnalytics from "@entities/PricingTracker/lib/hooks/useDownloadPricingAnalytics";
import useGetPricingAnalytics from "@entities/PricingTracker/lib/hooks/useGetPricingAnalytics";
import useupdatePricingAnalytics from "@entities/PricingTracker/lib/hooks/useupdatePricingAnalytics";
import { IPricingInfo } from "@entities/PricingTracker/lib/types";
import { useGetAdmins } from "@entities/admin";
import { useGetBrands } from "@entities/brand";
import { useGetBriefs } from "@entities/brief/lib/hooks/useGetBriefs";
import { PerformanceTile } from "@features/ReportPerformanceMetrics/PerformanceTile";
import { BriefCreatorColumn } from "@features/breifCreatorColumn/BriefCreatorColumn";
import { BriefInfluencerCommercials } from "@features/briefInfluencerCommercials/BriefInfluencerCommercials";
import { BriefTimelinePopOver } from "@features/briefTimeline/BriefTimelinePopOver";
import { ToastService } from "@shared/lib/services";
import { ICost } from "@shared/lib/types/briefInfluencerV2";
import { isNumber } from "@shared/lib/utils/isValueExists";
import { safeDivide, sumOf } from "@shared/lib/utils/mathHelper";
import { DeliverablePillSummary, Table, TableTitleLayout } from "@shared/ui";
import { ColumnFilterWrapper } from "@shared/ui/filters/ColumnFilterWrapper";
import { ColumnSortUI } from "@shared/ui/filters/ColumnSortUI";
import CustomSelectFilter from "@shared/ui/filters/CustomSelectFilter";
import { format } from "date-fns";
import dayjs from "dayjs";
import { useDebounce } from "use-debounce";
import { ChartView } from "./ChartView";

const { RangePicker } = DatePicker;

export function PricingTracker() {
  const [dateRange, setDateRange] = useState<
    [dayjs.Dayjs | null, dayjs.Dayjs | null]
  >([
    dayjs().startOf("month").startOf("day"),
    dayjs().endOf("month").endOf("day"),
  ]);

  const [isCharViewVisible, setIsCharViewVisible] = useState(false);

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<IPricingInfo[]>([]);

  const [campaignIds, setCampaignIds] = useState<string[]>([]);
  const [brandIds, setBrandIds] = useState<string[]>([]);
  const [assignedPoc, setAssignedPoc] = useState<string[]>([]);

  const [isCostCheckedByPricing, setIsCostCheckedByPricing] =
    useState<string>("all");

  const [sortState, setSortState] = useState<{
    field: string;
    order: "asc" | "desc";
  } | null>(null);

  const {
    pricingAnalytics,
    pricingAnalyticsError,
    isPricingAnalyticsFetching,
  } = useGetPricingAnalytics({
    from_to: {
      from: dateRange[0]?.startOf("day").toISOString() || "",
      to: dateRange[1]?.endOf("day").toISOString() || "",
    },
    assignedPricingPocs: assignedPoc,
    brandIds: brandIds,
    briefIds: campaignIds,
    isCostCheckedByPricing:
      isCostCheckedByPricing === "all"
        ? undefined
        : isCostCheckedByPricing === "yes"
          ? true
          : false,
  });

  const {
    updatePricingAnalytics,
    isUpdatingPricingAnalytics,
    updatingPricingAnalyticsVariables,
    isUpdatingPricingAnalyticsSuccess,
  } = useupdatePricingAnalytics();

  useEffect(() => {
    if (isUpdatingPricingAnalyticsSuccess) {
      ToastService.success("Pricing analytics updated successfully");
      setSelectedRowKeys([]);
    }
  }, [isUpdatingPricingAnalyticsSuccess]);

  useEffect(() => {
    setSelectedRowKeys([]);
    setSelectedRows([]);
  }, [
    brandIds,
    campaignIds,
    assignedPoc,
    isCostCheckedByPricing,
    JSON.stringify(dateRange),
  ]);

  const {
    downloadPricingAnalytics,
    isDownloadingPricingAnalytics,
    isDownloadingPricingAnalyticsSuccess,
  } = useDownloadPricingAnalytics();

  const [searchQuery, setSearchQuery] = useState<{
    campaign: string;
    brand: string;
    assignedPoc: string;
  }>({
    campaign: "",
    brand: "",
    assignedPoc: "",
  });

  const [debouncedSearch] = useDebounce(searchQuery, 250);

  const { briefs, isFetching: isBriefsFetching } = useGetBriefs({
    pageNo: 1,
    limit: 100,
    prospectBudget: 0,
    confidence: 0,
    status: undefined,
    planningStatus: undefined,
    pricingStatus: undefined,
    campaignByBrandIds: undefined,
    accountManagersBrandIds: undefined,
    searchQuery: debouncedSearch.campaign,
    sortBy: undefined,
    mainStatus: ["Campaign"],
  });

  const { brands, isFetching: isBrandsFetching } = useGetBrands(
    100,
    1,
    debouncedSearch.brand,
    undefined,
    undefined,
    undefined,
    undefined
  );

  const { admins, isFetching } = useGetAdmins(
    100,
    1,
    undefined,
    debouncedSearch.assignedPoc,
    undefined,
    undefined,
    undefined
  );

  // const { pricingAnalyticsFacets, pricingAnalyticsFacetsError } =
  //   useGetPricingAnalyticsFacets({});

  const [deliverableModalProps, setDeliverableModalProps] = useState<{
    visible: boolean;
    deliverableId?: string;
    briefInfluencerIds: string[];
    briefId?: string;
    type: "view" | "create";
  }>({
    visible: false,
    deliverableId: "",
    briefInfluencerIds: [],
    type: "create",
    briefId: "",
  });

  const campaignsToDisplay =
    briefs?.map((item) => {
      return { value: item._id, label: item.name };
    }) || [];

  const brandsToDisplay =
    brands?.data?.map((item) => {
      return { value: item._id, label: item.name };
    }) || [];

  const assignedPocs =
    admins?.data?.map((item) => {
      return {
        value: item._id,
        label: item.firstName + " " + item.lastName,
      };
    }) || [];

  const handleCampaignChange = (value: string[]) => {
    if (value === undefined) {
      setCampaignIds([]);
    } else {
      setCampaignIds(value);
    }
  };

  const sortedData = useMemo(() => {
    const data = [...(pricingAnalytics?.data?.data || [])];
    if (!sortState) return data;

    const { field, order } = sortState;
    const multiplier = order === "asc" ? 1 : -1;

    return data.sort((a, b) => {
      let valA: number = 0;
      let valB: number = 0;

      if (field === "influencerCost") {
        valA = isNumber(a.influencerCost?.cost)
          ? (a.influencerCost?.cost as number)
          : 0;
        valB = isNumber(b.influencerCost?.cost)
          ? (b.influencerCost?.cost as number)
          : 0;
      } else if (field === "brandCost") {
        valA = isNumber(a.brandCost?.cost) ? (a.brandCost?.cost as number) : 0;
        valB = isNumber(b.brandCost?.cost) ? (b.brandCost?.cost as number) : 0;
      } else if (field === "serviceCost") {
        valA = sumOf(
          a?.brandLedger?.filter((ele) => ele?.type === "Debit") || [],
          "serviceCost"
        );
        valB = sumOf(
          b?.brandLedger?.filter((ele) => ele?.type === "Debit") || [],
          "serviceCost"
        );
      } else if (field === "margin") {
        valA = isNumber(a.margin) ? (a.margin as number) : 0;
        valB = isNumber(b.margin) ? (b.margin as number) : 0;
      } else if (field === "companyMargin") {
        const marginA = isNumber(a.margin) ? a.margin : 0;
        const marginB = isNumber(b.margin) ? b.margin : 0;
        const serviceCostA = sumOf(
          a?.brandLedger?.filter((ele) => ele?.type === "Debit") || [],
          "serviceCost"
        );
        const serviceCostB = sumOf(
          b?.brandLedger?.filter((ele) => ele?.type === "Debit") || [],
          "serviceCost"
        );
        valA = marginA + serviceCostA;
        valB = marginB + serviceCostB;
      } else if (field === "marginPercentage") {
        valA = isNumber(a.marginPercentage)
          ? (a.marginPercentage as number)
          : 0;
        valB = isNumber(b.marginPercentage)
          ? (b.marginPercentage as number)
          : 0;
      } else if (field === "influencerNegoDifference") {
        valA = isNumber(a.influencerNegoDifference)
          ? (a.influencerNegoDifference as number)
          : 0;
        valB = isNumber(b.influencerNegoDifference)
          ? (b.influencerNegoDifference as number)
          : 0;
      } else if (field === "influencerNegoDifferencePercentage") {
        valA = isNumber(a.influencerNegoDifferencePercentage)
          ? (a.influencerNegoDifferencePercentage as number)
          : 0;
        valB = isNumber(b.influencerNegoDifferencePercentage)
          ? (b.influencerNegoDifferencePercentage as number)
          : 0;
      }

      return (valA - valB) * multiplier;
    });
  }, [pricingAnalytics?.data?.data, sortState]);

  const getSortColumnProps = (fieldKey: string, titleName: string) => {
    const isSorted = sortState?.field === fieldKey;
    const sortOrder: SortOrder = isSorted
      ? sortState.order === "asc"
        ? "ascend"
        : "descend"
      : null;

    return {
      sorter: true,
      sortOrder,
      filterCount: isSorted ? 1 : 0,
      filterDropdownProps: { destroyPopupOnHide: true },
      filterDropdown: (props: { confirm: () => void; close: () => void }) => {
        return (
          <ColumnFilterWrapper
            title={titleName}
            filterCount={isSorted ? 1 : 0}
            sortActive={isSorted}
            sortDirection={isSorted ? sortState.order : undefined}
            onReset={() => {
              setSortState(null);
              props.confirm();
            }}
            onApply={() => {
              props.confirm();
            }}
            onClose={props.close}
            hasSort={true}
            hasFilter={false}
          >
            <ColumnSortUI
              sortActive={isSorted}
              sortDirection={isSorted ? sortState.order : undefined}
              onSortChange={(dir) => {
                if (!dir) {
                  setSortState(null);
                } else {
                  setSortState({ field: fieldKey, order: dir });
                }
              }}
            />
          </ColumnFilterWrapper>
        );
      },
    };
  };

  const handleBrandChange = (value: string[]) => {
    if (value === undefined) {
      setBrandIds([]);
    } else {
      setBrandIds(value);
    }
  };

  const handleAssignedPocChange = (value: string[]) => {
    if (value === undefined) {
      setAssignedPoc([]);
    } else {
      setAssignedPoc(value);
    }
  };

  const columns: ColumnsType<IPricingInfo> = [
    {
      title: "Creator",
      dataIndex: "influencerName",
      key: "creator",
      fixed: "left",
      width: 200,
      className: styles.actionsColumn,
      render: (text, record) => (
        <BriefCreatorColumn
          influencerId={record.influencerId}
          influencerName={record.influencerName}
          username={record.socialMedia?.username}
          platform={record.socialMedia?.platform}
          profilePicUrl={record.socialMedia?.profilePicUrl}
          link={record.socialMedia?.link}
          isCreatorDeclined={record.isCreatorDeclined}
          isBrandDeclined={record.isBrandDeclined}
          creatorDeclinedReason={record.creatorDeclinedReason}
          brandDeclinedReason={record.brandDeclinedReason}
        />
      ),
    },

    {
      title: "Campaign Name",
      dataIndex: "brief",
      key: "brief",
      render: (text, record) => <span>{record.brief?.name}</span>,
      filterCount: campaignIds.length,
      filterDropdownProps: {
        destroyPopupOnHide: true,
      },
      filterDropdown: (props: { confirm: () => void; close: () => void }) => (
        <CustomSelectFilter
          title="Campaign Name"
          options={campaignsToDisplay}
          loading={isBriefsFetching}
          onSearch={(value) => {
            setSearchQuery((prev) => ({
              ...prev,
              campaign: value,
            }));
          }}
          onApply={handleCampaignChange}
          selectedValues={campaignIds}
          placeholder="Select campaigns"
          close={() => {
            props.close();
            setSearchQuery((prev) => ({ ...prev, campaign: "" }));
          }}
          confirm={props.confirm}
        />
      ),
    },

    {
      title: "Brand Name",
      dataIndex: "brand",
      key: "brand",
      render: (text, record) => <span>{record.brand?.name}</span>,
      filterCount: brandIds.length,
      filterDropdownProps: {
        destroyPopupOnHide: true,
      },
      filterDropdown: (props: { confirm: () => void; close: () => void }) => (
        <CustomSelectFilter
          title="Brand Name"
          options={brandsToDisplay}
          loading={isBrandsFetching}
          onSearch={(value) => {
            setSearchQuery((prev) => ({
              ...prev,
              brand: value,
            }));
          }}
          onApply={handleBrandChange}
          selectedValues={brandIds}
          placeholder="Select brands"
          close={() => {
            props.close();
            setSearchQuery((prev) => ({ ...prev, brand: "" }));
          }}
          confirm={props.confirm}
        />
      ),
    },

    {
      title: "Assigned POC",
      dataIndex: "assignedPricingPocV2",
      key: "assignedPricingPocV2",
      render: (text, record) =>
        record.assignedPricingPocV2?.assignedToUser?.firstName &&
        record.assignedPricingPocV2?.assignedToUser?.lastName ? (
          <span>{`${record.assignedPricingPocV2?.assignedToUser?.firstName} ${record.assignedPricingPocV2?.assignedToUser?.lastName}`}</span>
        ) : (
          "Unassigned"
        ),
      filterCount: assignedPoc.length,
      filterDropdownProps: {
        destroyPopupOnHide: true,
      },
      filterDropdown: (props: { confirm: () => void; close: () => void }) => (
        <CustomSelectFilter
          title="Assigned POC"
          options={assignedPocs}
          loading={isFetching}
          onSearch={(value) => {
            setSearchQuery((prev) => ({
              ...prev,
              assignedPoc: value,
            }));
          }}
          onApply={handleAssignedPocChange}
          selectedValues={assignedPoc}
          placeholder="Select POCs"
          close={() => {
            props.close();
            setSearchQuery((prev) => ({ ...prev, assignedPoc: "" }));
          }}
          confirm={props.confirm}
        />
      ),
    },

    {
      title: "Deliverables",
      key: "deliverables",
      width: 200,
      render: (_, record) => {
        if (record.isDeliverablesPending) {
          return <span>No Deliverables</span>;
        }

        if (
          record?.deliverableV2 &&
          record.deliverableV2.purchaseItems &&
          record.deliverableV2.purchaseItems.length > 0
        ) {
          return (
            <Flex align="center" gap={2}>
              <DeliverablePillSummary deliverable={record?.deliverableV2} />
            </Flex>
          );
        }
        return <div>..</div>;
      },
    },
    {
      title: "Influencer Cost",
      key: "influencerCost",
      dataIndex: "influencerCost",
      ...getSortColumnProps("influencerCost", "Influencer Cost"),
      render: (_, record) => {
        if (record.isBrandDeclined) {
          return (
            <Tooltip title={record?.brandDeclinedReason}>
              <Button
                type="link"
                danger
                icon={<InfoCircleOutlined />}
                className={styles.tooltipButton}
              >
                Brand Declined
              </Button>
            </Tooltip>
          );
        }
        if (record.isCreatorDeclined) {
          return (
            <Tooltip title={record?.creatorDeclinedReason}>
              <Button
                type="link"
                danger
                icon={<InfoCircleOutlined />}
                className={styles.tooltipButton}
              >
                Creator Declined
              </Button>
            </Tooltip>
          );
        }
        return <BriefInfluencerCommercials briefInfluencer={record} />;
      },
    },
    {
      title: "Brand Cost",
      key: "brandCost",
      dataIndex: "brandCost",
      ...getSortColumnProps("brandCost", "Brand Cost"),
      render: (text, record) => (
        <Space>
          <BriefTimelinePopOver briefInfluencer={record} type="brand" />
          <span>{record?.brandCost?.cost}</span>
        </Space>
      ),
    },
    {
      title: "Service Cost",
      key: "serviceCost",
      dataIndex: "serviceCost",
      ...getSortColumnProps("serviceCost", "Service Cost"),
      render: (text, record) => (
        <span>
          {sumOf(
            record?.brandLedger?.filter((ele) => ele?.type === "Debit") || [],
            "serviceCost"
          )}
        </span>
      ),
    },
    {
      title: "Margin",
      key: "margin",
      dataIndex: "margin",
      ...getSortColumnProps("margin", "Margin"),
    },
    {
      title: "Company Margin",
      key: "companyMargin",
      dataIndex: "companyMargin",
      ...getSortColumnProps("companyMargin", "Company Margin"),
      render: (_, record) => {
        const margin = record?.margin || 0;
        const serviceCost = sumOf(
          record?.brandLedger?.filter((ele) => ele?.type === "Debit") || [],
          "serviceCost"
        );
        return (margin + serviceCost).toFixed(2);
      },
    },
    {
      title: "Margin Percentage",
      key: "marginPercentage",
      dataIndex: "marginPercentage",
      ...getSortColumnProps("marginPercentage", "Margin Percentage"),
      render: (_, record) => `${record?.marginPercentage?.toFixed(2)}%`,
    },
    {
      title: "Influencer Nego Difference",
      key: "influencerNegoDifference",
      dataIndex: "influencerNegoDifference",
      ...getSortColumnProps(
        "influencerNegoDifference",
        "Influencer Nego Difference"
      ),
    },
    {
      title: "Influencer Nego Difference Percentage",
      key: "influencerNegoDifferencePercentage",
      dataIndex: "influencerNegoDifferencePercentage",
      ...getSortColumnProps(
        "influencerNegoDifferencePercentage",
        "Influencer Nego Difference Percentage"
      ),
      render: (_, record) =>
        `${record?.influencerNegoDifferencePercentage?.toFixed(2)}%`,
    },
    {
      title: "Purchased At",
      key: "influencerPurchasedAt",
      dataIndex: "influencerPurchasedAt",
      render: (_, record) =>
        record?.influencerCost?.createdAt
          ? format(record?.influencerCost?.createdAt, "dd MMM yy")
          : "",
    },
    {
      title: "Is Cost Checked By Pricing",
      key: "isCostCheckedByPricing",
      dataIndex: "isCostCheckedByPricing",
      filterCount: isCostCheckedByPricing !== "all" ? 1 : 0,
      filterDropdownProps: { destroyPopupOnHide: true },
      filterDropdown: (props: { confirm: () => void; close: () => void }) => (
        <ColumnFilterWrapper
          title="Is Cost Checked By Pricing"
          filterCount={isCostCheckedByPricing !== "all" ? 1 : 0}
          onReset={() => {
            setIsCostCheckedByPricing("all");
            props.confirm();
          }}
          onApply={() => {
            props.confirm();
          }}
          onClose={props.close}
        >
          <div style={{ padding: "8px 4px" }}>
            <Select
              style={{ width: "100%" }}
              options={[
                { label: "Yes", value: "yes" },
                { label: "No", value: "no" },
                { label: "All", value: "all" },
              ]}
              value={isCostCheckedByPricing}
              onChange={(value) => setIsCostCheckedByPricing(value)}
            />
          </div>
        </ColumnFilterWrapper>
      ),
      render: (_, record) => (
        <>
          <Button
            type="link"
            loading={
              isUpdatingPricingAnalytics &&
              updatingPricingAnalyticsVariables?.briefInfluencerIds.includes(
                record.briefInfluencerId
              )
            }
            disabled={record.isCostCheckedByPricing}
            onClick={() =>
              updatePricingAnalytics({
                briefInfluencerIds: [record._id],
              })
            }
          >
            {record.isCostCheckedByPricing ? "Approved" : "Approve"}
          </Button>
        </>
      ),
    },
  ];

  useEffect(() => {
    if (isDownloadingPricingAnalyticsSuccess) {
      ToastService.success("Pricing analytics downloaded successfully");
    }
  }, [isDownloadingPricingAnalyticsSuccess]);

  const companyMargin = pricingAnalytics?.data?.data
    ? sumOf(pricingAnalytics?.data?.data, "margin") +
      sumOf(
        pricingAnalytics?.data?.data?.flatMap(
          (ele) =>
            ele?.brandLedger?.filter((ele) => ele?.type === "Debit") || []
        ),
        "serviceCost"
      )
    : 0;

  const brandCost = pricingAnalytics?.data?.data
    ? sumOf(
        pricingAnalytics?.data?.data
          ?.filter((ele) => Boolean(ele))
          ?.map((ele) => ele?.brandCost as ICost),
        "cost"
      )
    : 0;

  const serviceCost = pricingAnalytics?.data?.data
    ? sumOf(
        pricingAnalytics?.data?.data
          ?.filter((ele) => Boolean(ele))
          ?.flatMap(
            (ele) =>
              ele?.brandLedger?.filter((ele) => ele?.type === "Debit") || []
          ),
        "serviceCost"
      )
    : 0;

  const companyMarginPercentage =
    safeDivide(companyMargin, brandCost + serviceCost) * 100;

  const onSelectChange = (
    newSelectedRowKeys: React.Key[],
    newSelectedRows: IPricingInfo[]
  ) => {
    setSelectedRowKeys(newSelectedRowKeys);
    setSelectedRows(newSelectedRows);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  return (
    <Flex vertical gap={20}>
      <h1 className={styles.title}>Pricing Tracker</h1>
      <Flex gap={20} wrap="wrap">
        <PerformanceTile
          title="Influencer Price"
          value={(pricingAnalytics?.data?.data
            ? sumOf(
                pricingAnalytics?.data?.data
                  ?.filter((ele) => Boolean(ele))
                  ?.map((ele) => ele?.influencerCost as ICost),
                "cost"
              )
            : 0
          ).toString()}
        />

        <PerformanceTile title="Brand Price" value={brandCost.toString()} />
        <Divider type="vertical" style={{ height: "auto" }} />

        <PerformanceTile title="Service Cost" value={serviceCost.toFixed(2)} />

        <PerformanceTile
          title="Service Cost Percentage"
          value={`${(pricingAnalytics?.data?.data
            ? safeDivide(
                serviceCost,
                sumOf(
                  pricingAnalytics?.data?.data
                    ?.filter((ele) => Boolean(ele))
                    ?.map((ele) => ele?.brandCost as ICost),
                  "cost"
                )
              ) * 100
            : 0
          ).toFixed(2)}%`}
        />

        <Divider type="vertical" style={{ height: "auto" }} />
        <PerformanceTile
          title="Margin"
          value={(pricingAnalytics?.data?.data
            ? sumOf(pricingAnalytics?.data?.data, "margin")
            : 0
          ).toString()}
        />

        <PerformanceTile
          title="Absolute Margin Percentage"
          value={`${(pricingAnalytics?.data?.data
            ? safeDivide(
                sumOf(
                  pricingAnalytics?.data?.data
                    ?.filter((ele) => Boolean(ele))
                    ?.map((ele) => ele?.brandCost as ICost),
                  "cost"
                ) -
                  sumOf(
                    pricingAnalytics?.data?.data
                      ?.filter((ele) => Boolean(ele))
                      ?.map((ele) => ele?.influencerCost as ICost),
                    "cost"
                  ),
                sumOf(
                  pricingAnalytics?.data?.data
                    ?.filter((ele) => Boolean(ele))
                    ?.map((ele) => ele?.brandCost as ICost),
                  "cost"
                )
              ) * 100
            : 0
          ).toFixed(2)}%`}
        />
        <Divider type="vertical" style={{ height: "auto" }} />
        <PerformanceTile
          title="Company Margin"
          value={companyMargin.toFixed(2)}
        />

        <PerformanceTile
          title="Company Margin Percentage"
          value={`${companyMarginPercentage.toFixed(2)}%`}
        />

        <Divider type="vertical" style={{ height: "auto" }} />

        <PerformanceTile
          title="Influencer Nego Difference"
          value={(pricingAnalytics?.data?.data
            ? sumOf(pricingAnalytics?.data?.data, "finalInfluencerCost") -
              sumOf(pricingAnalytics?.data?.data, "firstInfluencerCost")
            : 0
          ).toString()}
        />

        <PerformanceTile
          title="Absolute Influencer Nego Percentage"
          value={`${(pricingAnalytics?.data?.data
            ? safeDivide(
                sumOf(pricingAnalytics?.data?.data, "finalInfluencerCost") -
                  sumOf(pricingAnalytics?.data?.data, "firstInfluencerCost"),
                sumOf(pricingAnalytics?.data?.data, "firstInfluencerCost")
              ) * 100
            : 0
          ).toFixed(2)}%`}
        />

        <PerformanceTile
          title="Actual Number of Influencer Negos"
          value={`${
            pricingAnalytics?.data?.data?.filter(
              (ele) => ele.finalInfluencerCost - ele.firstInfluencerCost !== 0
            ).length || 0
          }`}
        />

        <Divider type="vertical" style={{ height: "auto" }} />
        <PerformanceTile
          title="No of Records"
          value={`${pricingAnalytics?.data?.data?.length || 0}`}
        />
      </Flex>
      <div style={{ position: "relative" }}>
        <Table
          rowSelection={rowSelection}
          dataSource={sortedData}
          title={() => (
            <TableTitleLayout
              filterComponent={
                <Flex justify="start" style={{ width: "100%" }}>
                  <Flex gap={20} style={{ width: "90%" }}>
                    {/* <Typography.Text>Select Date Range</Typography.Text> */}
                    <Space>
                      <RangePicker
                        value={
                          dateRange[0] && dateRange[1]
                            ? [dayjs(dateRange[0]), dayjs(dateRange[1])]
                            : undefined
                        }
                        onChange={(dates) => {
                          setDateRange(
                            dates as [dayjs.Dayjs | null, dayjs.Dayjs | null]
                          );
                        }}
                        format="DD MMM YYYY"
                        allowClear={false}
                      />
                      <InfoCircleOutlined title="Please ensure that the range doesn't exceed 30 days." />
                    </Space>
                  </Flex>
                  <Space size={4}>
                    <Button
                      type="primary"
                      onClick={() => setIsCharViewVisible(true)}
                    >
                      View Chart
                    </Button>
                    <Button
                      type="primary"
                      onClick={() =>
                        downloadPricingAnalytics({
                          from_to: {
                            from: dateRange[0]?.toString() || "",
                            to: dateRange[1]?.toString() || "",
                          },
                          assignedPricingPocs: assignedPoc,
                          brandIds: brandIds,
                          briefIds: campaignIds,
                        })
                      }
                      loading={isDownloadingPricingAnalytics}
                      disabled={isDownloadingPricingAnalytics}
                      icon={<DownloadOutlined />}
                    >
                      Download
                    </Button>
                  </Space>
                </Flex>
              }
            />
          )}
          size="small"
          className={styles.singleLineHeaders}
          columns={columns}
          rowKey={(record) => record._id}
          onChange={(_pagination, _filters, sorter) => {
            const currentSorter = Array.isArray(sorter) ? sorter[0] : sorter;
            if (
              currentSorter &&
              currentSorter.columnKey &&
              currentSorter.order
            ) {
              setSortState({
                field: String(currentSorter.columnKey),
                order: currentSorter.order === "ascend" ? "asc" : "desc",
              });
            } else {
              setSortState(null);
            }
          }}
          loading={isPricingAnalyticsFetching}
          scroll={{
            x: "max-content",
          }}
        />
        {selectedRowKeys.length > 0 && (
          <div className={styles.selectedRowActionsContainer} style={{}}>
            <Button
              type="primary"
              onClick={() =>
                updatePricingAnalytics({
                  briefInfluencerIds: selectedRows
                    .filter((item) => item.isCostCheckedByPricing)
                    .map((item) => item._id),
                })
              }
              loading={isUpdatingPricingAnalytics}
              disabled={selectedRowKeys.length === 0}
            >
              Approve ({selectedRowKeys.length})
            </Button>
          </div>
        )}
      </div>
      <style>{`.ant-table-row-selected >.ant-table-cell { background: var(--hover-bg) 
      !important; }`}</style>
      {/* <Modal
        title={
          deliverableModalProps.type === "create"
            ? "Select Deliverable"
            : deliverableModalProps.type === "view"
              ? "View Deliverable"
              : "Edit Deliverable"
        }
        open={deliverableModalProps.visible}
        onCancel={() =>
          setDeliverableModalProps({
            visible: false,
            type: "create",
            deliverableId: "",
            briefInfluencerIds: [],
            briefId: "",
          })
        }
        footer={null}
        width={800}
        destroyOnClose
        bodyStyle={{ padding: 0 }}
      >
        {deliverableModalProps.visible && (
          <>
            {deliverableModalProps.type === "create" ? (
              <CampaignDeliverables
                isEditable={true}
                briefInfluencerIds={deliverableModalProps.briefInfluencerIds}
              />
            ) : (
              <DeliverablePackageCard
                briefId={deliverableModalProps?.briefId || ""}
                onClose={() =>
                  setDeliverableModalProps({
                    visible: false,
                    type: "create",
                    deliverableId: "",
                    briefInfluencerIds: [],
                  })
                }
                deliverableId={deliverableModalProps.deliverableId || ""}
              />
            )}
          </>
        )}
      </Modal> */}
      <Modal
        title="Pricing Analytics Chart"
        open={isCharViewVisible}
        onCancel={() => setIsCharViewVisible(false)}
        footer={null}
        width={800}
        destroyOnClose
      >
        <ChartView
          data={pricingAnalytics?.data?.data || []}
          dateRange={dateRange}
        />
      </Modal>
    </Flex>
  );
}
